/**
 * /select-org — paso intermedio OBLIGATORIO post-login cuando el usuario
 * pertenece a más de una empresa.
 *
 * Flujo (replicado de Lex Account):
 *   1. login emite tokens con la empresa default (la primera por created_at)
 *   2. SPA detecta memberships.length > 1 → seteea sessionStorage flag y
 *      navega aquí
 *   3. ProtectedRoute redirige aquí desde cualquier otra ruta mientras el
 *      flag esté presente (cierra el bypass por refresh)
 *   4. Usuario elige empresa (radio buttons) → POST /auth/switch-org
 *   5. Backend revoca la sesión actual + emite nuevos tokens con esa org
 *   6. SPA limpia el flag, guarda el nuevo access_token, navega al dashboard
 *
 * Si solo tiene 1 empresa, este paso no se invoca (el SPA va directo al
 * dashboard). Si no tiene ninguna empresa (USER puro), tampoco se invoca.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Check, Loader2, LogOut, ShieldCheck, UserCircle2 } from 'lucide-react'
import { toast } from 'sonner'

import { ThemeToggle } from '@/components/ui/theme-toggle'
import { useCurrentUser } from '@/hooks/api/use-current-user'
import { useLogout } from '@/hooks/use-logout'
import { useSwitchOrg } from '@/hooks/use-switch-org'
import { APP, ROUTES } from '@/lib/constants'
import { ORG_SELECTION_FLAG, clearOrgSelectionFlag } from '@/lib/org-selection'

export function SelectOrgPage() {
  const navigate = useNavigate()
  const { data: me, isLoading } = useCurrentUser()
  const switchOrg = useSwitchOrg()
  const logout = useLogout()
  const [picked, setPicked] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const memberships = useMemo(() => me?.memberships ?? [], [me])

  // Edge case: usuario sin empresas (USER puro). El selector no aplica,
  // lo enviamos directo al dashboard y borramos el flag por si quedó.
  useEffect(() => {
    if (!me) return
    if (memberships.length === 0) {
      clearOrgSelectionFlag()
      navigate(ROUTES.DASHBOARD, { replace: true })
    }
  }, [me, memberships.length, navigate])

  // Pre-selecciona la org actual de la sesión si existe.
  useEffect(() => {
    if (!picked && memberships.length > 0) {
      const current = memberships.find((m) => m.is_active_session_org)
      setPicked(current?.organization_id ?? memberships[0].organization_id)
    }
  }, [memberships, picked])

  const onConfirm = async () => {
    if (!picked || submitting) return
    const target = memberships.find((m) => m.organization_id === picked)
    if (!target) return
    setSubmitting(true)
    try {
      // Si la org elegida ya es la activa de la sesión, ahorramos el
      // round-trip a /switch-org. Solo limpiamos el flag y entramos
      // por client-side navigation — el access token en memoria se
      // preserva.
      if (target.is_active_session_org) {
        clearOrgSelectionFlag()
        navigate(ROUTES.DASHBOARD, { replace: true })
        return
      }
      // Si SÍ hay switch real: switchOrg ya guardó el nuevo access
      // token en memoria + qc.clear(). Hacemos client-side navigation
      // (no full reload) para preservar ese token. El SPA hidratará
      // useCurrentUser con el token nuevo y la org cambiada.
      await switchOrg(picked)
      clearOrgSelectionFlag()
      toast.success(
        `Entrando a ${target.organization?.razon_social ?? 'la empresa'}…`,
      )
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos cambiar de empresa')
      setSubmitting(false)
    }
  }

  const onGoToAccount = () => {
    // Acceso a "Mi cuenta" (hub de identidad de Taxor — /account, no
    // /profile). /account es el dashboard tipo Lex Account: cards de
    // identidad/seguridad/empresas/equipo. Desde ahí cada card hace
    // deep-link a /profile?tab=... para la sección concreta.
    //
    // Limpia el flag para que ProtectedRoute no rebote de vuelta acá.
    // Usa client-side navigation — el access token en memoria se
    // preserva, si fuera full reload (window.location) lo perderíamos
    // y caeríamos a /login.
    clearOrgSelectionFlag()
    navigate(ROUTES.ACCOUNT, { replace: true })
  }

  const onCancel = async () => {
    clearOrgSelectionFlag()
    await logout()
  }

  if (isLoading || !me) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
          <p className="text-sm text-fg-muted">Cargando tus empresas…</p>
        </div>
      </Shell>
    )
  }

  const isSingle = memberships.length === 1
  return (
    <Shell>
      <header className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand-500/10 text-brand-600">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-fg-primary">
          {isSingle ? 'Confirma con qué vas a trabajar' : 'Elige la empresa con la que vas a trabajar'}
        </h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Hola <strong>{me.first_name ?? me.email}</strong>.{' '}
          {isSingle ? (
            <>Continúa al panel de operación o entra a tu cuenta de Taxor para gestionar
            identidad, sesiones y seguridad.</>
          ) : (
            <>Tienes acceso a {memberships.length} empresas. Selecciona una para
            continuar — podrás cambiar después desde el header del panel.</>
          )}
        </p>
      </header>

      <ul role="radiogroup" className="mt-8 space-y-2">
        {memberships.map((m) => {
          const org = m.organization
          if (!org) return null
          const selected = picked === m.organization_id
          return (
            <li key={m.membership_id}>
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
                  selected
                    ? 'border-brand-500 bg-brand-500/5'
                    : 'border-border bg-bg-surface hover:border-fg-muted'
                }`}
              >
                <input
                  type="radio"
                  name="org"
                  value={m.organization_id}
                  checked={selected}
                  onChange={() => setPicked(m.organization_id)}
                  className="mt-1 h-4 w-4 shrink-0 border-border text-brand-600 focus:ring-2 focus:ring-brand-500/20"
                />
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-bg-app text-fg-muted">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <p className="text-sm font-semibold text-fg-primary">
                      {org.razon_social}
                    </p>
                    {org.nombre_comercial && (
                      <span className="text-xs text-fg-muted">
                        ({org.nombre_comercial})
                      </span>
                    )}
                    {!org.is_active && (
                      <span className="rounded-full bg-warning-tint px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-fg">
                        Inactiva
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-fg-muted">
                    {org.entity_type === 'company' ? 'NIT' : 'Doc'} {org.tax_id}
                    <span className="px-1">·</span>
                    <span className="font-mono uppercase tracking-wider text-fg-secondary">
                      {m.role}
                    </span>
                  </p>
                </div>
                {selected && (
                  <Check className="mt-1 h-4 w-4 shrink-0 text-brand-600" />
                )}
              </label>
            </li>
          )
        })}
      </ul>

      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={!picked || submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 px-6 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Entrando…
            </>
          ) : (
            <>
              Entrar al panel
              <span aria-hidden>→</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={onGoToAccount}
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-bg-surface px-6 py-2.5 text-sm font-medium text-fg-primary transition-colors hover:bg-bg-elevated disabled:opacity-50"
        >
          <UserCircle2 className="h-4 w-4" />
          Ir a Mi cuenta de Taxor
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm text-fg-muted transition-colors hover:text-fg-secondary disabled:opacity-50"
        >
          <LogOut className="h-4 w-4" />
          Cerrar sesión
        </button>
      </div>

      <p className="mt-6 text-center text-xs text-fg-muted">
        {isSingle
          ? '"Mi cuenta" es tu panel de identidad: perfil, sesiones, contraseña, 2FA, empresas y privacidad.'
          : 'Cambiar de empresa cierra tu sesión actual y emite una nueva con la empresa elegida. Una sola sesión activa por usuario a la vez.'}
      </p>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app px-4">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex items-center justify-between">
          <a
            href={import.meta.env.VITE_APP_URL ?? 'https://taxor.co'}
            className="flex items-center gap-2"
          >
            <div
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-brand-600 font-mono text-sm font-bold text-white shadow-sm"
            >
              Tx
            </div>
            <span className="text-base font-semibold text-fg-primary">
              {APP.name}
            </span>
          </a>
          <ThemeToggle />
        </div>
        <div className="rounded-xl border border-border bg-bg-surface p-8 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  )
}

export { ORG_SELECTION_FLAG }
